import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import ApexGlyph from './ApexGlyph';

/**
 * GovernanceContinuum
 * -------------------
 * Temporal state visualization. Four parallel proof-layer rails carry integrity
 * pulses through time. In `degraded` mode the rails drift apart, the control-state
 * rail fractures (amber crosshatch), and the affected decision/oversight nodes
 * pulse amber.
 *
 *   ───────●──────●─────────────────●───────────  Classification (indigo)
 *   ─────────●─────────⚠──────●──────────────     Control State (teal — the moat)
 *   ──────────────●─────────────●────────────     Decision     (slate)
 *   ─────────────────────●────────────●──────     Oversight    (deep slate)
 *
 * Pure SVG + SMIL animateMotion. Used as:
 *   • ProductLens   atmospheric separator   variant='lens'   degraded=false
 *   • ProductTrust  atmospheric separator   variant='trust'  degraded=true
 *   • GovernanceReplay backdrop (degrades when the user toggles Apr 14 → Apr 15)
 */

const TEAL   = '#0D9488';
const INDIGO = '#6366F1';
const AMBER  = '#F59E0B';
const SLATE  = '#94A3B8';
const SLATE_DEEP = '#475569';

const RAILS_DEFAULT = [
  { id: 'classification', label: 'Classification', color: INDIGO,     y: 0.20, speed: 28 },
  { id: 'control',        label: 'Control State',  color: TEAL,       y: 0.40, speed: 22 },
  { id: 'decision',       label: 'Decision',       color: SLATE,      y: 0.62, speed: 32 },
  { id: 'oversight',      label: 'Oversight',      color: SLATE_DEEP, y: 0.80, speed: 25 },
];

// Lens — spacious / environmental cadence (cloud topology feel).
// Wider rail spacing, slower pulses.
const RAILS_LENS = [
  { id: 'classification', label: 'Classification', color: INDIGO,     y: 0.18, speed: 35 },
  { id: 'control',        label: 'Control State',  color: TEAL,       y: 0.42, speed: 28 },
  { id: 'decision',       label: 'Decision',       color: SLATE,      y: 0.66, speed: 40 },
  { id: 'oversight',      label: 'Oversight',      color: SLATE_DEEP, y: 0.84, speed: 32 },
];

// Trust — tight / precise cadence (decision forensics feel).
// Rails closer together, faster pulses.
const RAILS_TRUST = [
  { id: 'classification', label: 'Classification', color: INDIGO,     y: 0.22, speed: 22 },
  { id: 'control',        label: 'Control State',  color: TEAL,       y: 0.40, speed: 18 },
  { id: 'decision',       label: 'Decision',       color: SLATE,      y: 0.58, speed: 26 },
  { id: 'oversight',      label: 'Oversight',      color: SLATE_DEEP, y: 0.76, speed: 20 },
];

function getRails(variant) {
  if (variant === 'lens')  return RAILS_LENS;
  if (variant === 'trust') return RAILS_TRUST;
  return RAILS_DEFAULT;
}

// Keep RAILS as an alias for legacy NODES references below (node positions
// indexed by rail order — order is identical across variants).
const RAILS = RAILS_DEFAULT;

const NODES = [
  { rail: 0, x: 0.15,  color: INDIGO,     isAlert: false },
  { rail: 0, x: 0.72,  color: INDIGO,     isAlert: false },
  { rail: 1, x: 0.22,  color: TEAL,       isAlert: false },
  { rail: 1, x: 0.51,  color: AMBER,      isAlert: true  }, // the gap
  { rail: 1, x: 0.68,  color: TEAL,       isAlert: false },
  { rail: 2, x: 0.35,  color: SLATE,      isAlert: false },
  { rail: 2, x: 0.60,  color: SLATE,      isAlert: false, alertWhenDegraded: true },
  { rail: 3, x: 0.45,  color: SLATE_DEEP, isAlert: false },
  { rail: 3, x: 0.80,  color: SLATE_DEEP, isAlert: false },
];

const W = 1200;
// Adaptive morph: duration scales linearly with the |Δseverity| magnitude.
// Tiny tweaks settle quickly; full clean↔fully-degraded eases longer.
const MORPH_MS_MIN = 1000;
const MORPH_MS_MAX = 2500;
const CLEAN_AMP = 2.2;

function buildPath(rail, index, height, severity) {
  const baseY = height * rail.y;
  // Per-rail degraded amplitude: rails further down drift further.
  const degradedAmp = 10 + index * 2.5;
  // Interpolate amplitude based on severity 0..1.
  const amp = CLEAN_AMP + (degradedAmp - CLEAN_AMP) * severity;
  const freq = 0.0045 + index * 0.0012;
  const phase = index * 0.85;

  const points = [];
  const segs = 28;
  for (let i = 0; i <= segs; i += 1) {
    const x = (i / segs) * W;
    const y = baseY + Math.sin(x * freq + phase) * amp;
    points.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return points.join(' ');
}

export default function GovernanceContinuum({
  height = 180,
  opacity = 1,
  degraded = false,
  severity,             // optional 0..1 — overrides `degraded` when provided
  variant = 'default',  // 'lens' | 'trust' | 'default'
  showLabels = false,
}) {
  // Resolve effective severity. Default: degraded boolean maps to 0 / 1.
  const effectiveSeverity = typeof severity === 'number'
    ? Math.max(0, Math.min(1, severity))
    : (degraded ? 1 : 0);

  // Variant-specific rail cadence — Lens spacious, Trust tight, default balanced.
  const rails = getRails(variant);

  // Unique id prefix so multiple Continuums on the same page don't collide with mpath refs.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  // Precompute clean (severity=0) and fully-degraded (severity=1) paths for each rail.
  // The <animate> element interpolates `d` between these endpoints via spline easing.
  const pathPairs = useMemo(
    () =>
      rails.map((rail, i) => ({
        id: rail.id,
        clean:    buildPath(rail, i, height, 0),
        degraded: buildPath(rail, i, height, 1),
        // The "current" path at the resolved severity — used as the static `d` and
        // as the animation's `to` endpoint.
        currentAt: (sev) => buildPath(rail, i, height, sev),
      })),
    [height, rails]
  );

  // Track previous severity so we can compute morph magnitude and direction.
  const prevSevRef = useRef(effectiveSeverity);
  const [morphKey, setMorphKey] = useState(0);
  const [hasToggled, setHasToggled] = useState(false);
  // Duration for the active morph — adaptive to the |Δseverity| magnitude.
  const [morphMs, setMorphMs] = useState(MORPH_MS_MAX);
  // Remember the source severity for the active morph (so SMIL `from` matches what we left).
  const [fromSev, setFromSev] = useState(effectiveSeverity);

  useEffect(() => {
    if (prevSevRef.current !== effectiveSeverity) {
      const delta = Math.abs(effectiveSeverity - prevSevRef.current);
      const dur = Math.round(MORPH_MS_MIN + delta * (MORPH_MS_MAX - MORPH_MS_MIN));
      setFromSev(prevSevRef.current);
      setMorphMs(dur);
      prevSevRef.current = effectiveSeverity;
      setHasToggled(true);
      setMorphKey((k) => k + 1);
    }
  }, [effectiveSeverity]);

  // CSS transition duration mirrors the SMIL morph so fracture/opacity transitions
  // ease in lockstep with the rail path morph.
  const cssMs = `${morphMs}ms`;

  return (
    <div
      data-testid="governance-continuum"
      data-degraded={effectiveSeverity >= 0.5 ? 'true' : 'false'}
      data-severity={effectiveSeverity.toFixed(2)}
      style={{ position: 'relative', width: '100%', opacity, overflow: 'hidden' }}
      aria-hidden
    >
      <svg
        viewBox={`0 0 ${W} ${height}`}
        width="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ height, display: 'block' }}
      >
        <defs>
          {/* Left / right vignettes */}
          <linearGradient id={`gc-vL-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#0A0A0F" stopOpacity="1" />
            <stop offset="14%" stopColor="#0A0A0F" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`gc-vR-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="86%"  stopColor="#0A0A0F" stopOpacity="0" />
            <stop offset="100%" stopColor="#0A0A0F" stopOpacity="1" />
          </linearGradient>

          <filter id={`gc-glow-${uid}`}>
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>

          {/* Named paths so animateMotion can mpath them.
              On `severity` change, an <animate> element re-mounts (keyed on morphKey)
              and morphs the d-attribute from the prior severity's path to the new one.
              Duration is adaptive — scales linearly with |Δseverity|. */}
          {rails.map((rail, i) => {
            const pair = pathPairs[i];
            const currentD = pair.currentAt(effectiveSeverity);
            const priorD   = pair.currentAt(fromSev);
            return (
              <path
                key={rail.id}
                id={`gc-${uid}-${rail.id}`}
                d={currentD}
              >
                {hasToggled && (
                  <animate
                    key={morphKey}
                    attributeName="d"
                    from={priorD}
                    to={currentD}
                    dur={`${morphMs}ms`}
                    fill="freeze"
                    calcMode="spline"
                    keySplines="0.4 0 0.2 1"
                  />
                )}
              </path>
            );
          })}
        </defs>

        {/* Rails */}
        {rails.map((rail, i) => {
          const isMoat = rail.id === 'control';
          return (
            <g key={rail.id}>
              <use
                href={`#gc-${uid}-${rail.id}`}
                fill="none"
                stroke={rail.color}
                strokeWidth={isMoat ? 1.8 : 1.1}
                strokeOpacity={isMoat ? 0.75 : 0.35}
              />
              {/* Integrity pulse — runs continuously along the rail */}
              <circle r="2" fill={rail.color} filter={`url(#gc-glow-${uid})`} fillOpacity="0.9">
                <animateMotion
                  dur={`${rail.speed}s`}
                  repeatCount="indefinite"
                  begin={`${-i * 4}s`}
                  calcMode="linear"
                >
                  <mpath href={`#gc-${uid}-${rail.id}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;0;0.85;0.85;0"
                  keyTimes="0;0.05;0.15;0.85;1"
                  dur={`${rail.speed}s`}
                  repeatCount="indefinite"
                  begin={`${-i * 4}s`}
                />
              </circle>
            </g>
          );
        })}

        {/* Governance nodes */}
        {NODES.map((node, i) => {
          const rail = rails[node.rail];
          // Approximate Y from the rail base (sinusoidal jitter is small enough to ignore for node placement).
          const x = node.x * W;
          const y = height * rail.y;
          const isAlertNow = node.isAlert || (effectiveSeverity > 0.4 && node.alertWhenDegraded);
          // Nodes that only exist in the degraded state — render them always but fade their opacity
          // so the morph reveals them gradually instead of popping in.
          const alertOnly = node.isAlert; // true for the Control-rail "gap" node
          // For alert-only nodes (the "gap"), interpolate visibility with severity so it appears
          // proportionally as the rails drift apart.
          const groupOpacity = alertOnly ? effectiveSeverity : 1;

          return (
            <g
              key={i}
              style={{
                animation: isAlertNow ? 'gc-node-pulse 1.6s ease-in-out infinite' : undefined,
                transformOrigin: `${x}px ${y}px`,
                opacity: groupOpacity,
                transition: `opacity ${cssMs} cubic-bezier(0.4,0,0.2,1)`,
              }}
            >
              <circle
                cx={x} cy={y} r="3.2"
                fill={isAlertNow ? AMBER : node.color}
                fillOpacity={isAlertNow ? 0.95 : 0.55}
                filter={isAlertNow ? `url(#gc-glow-${uid})` : undefined}
                style={{ transition: `fill ${cssMs} ease, fill-opacity ${cssMs} ease` }}
              />
              <circle
                cx={x} cy={y} r="7"
                fill="none"
                stroke={isAlertNow ? AMBER : node.color}
                strokeOpacity={isAlertNow ? 0.4 : 0.18}
                strokeWidth="1"
                style={{ transition: `stroke ${cssMs} ease, stroke-opacity ${cssMs} ease` }}
              />
              {showLabels && (
                <text
                  x={x} y={y - 12}
                  textAnchor="middle"
                  fill={isAlertNow ? AMBER : node.color}
                  fillOpacity="0.5"
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '7px', letterSpacing: '0.1em' }}
                >
                  ◆
                </text>
              )}
            </g>
          );
        })}

        {/* Degraded state: amber crosshatch fracture across the control-state rail.
            Always rendered, opacity scales with severity so it blooms gradually. */}
        <g
          data-testid="gc-fracture"
          style={{
            opacity: effectiveSeverity * 0.8,
            transition: `opacity ${cssMs} cubic-bezier(0.4,0,0.2,1)`,
          }}
        >
          <line
            x1={W * 0.45} y1={height * 0.32} x2={W * 0.65} y2={height * 0.48}
            stroke={AMBER} strokeWidth="1" strokeOpacity="0.28" strokeDasharray="4 6"
          />
          <line
            x1={W * 0.45} y1={height * 0.48} x2={W * 0.65} y2={height * 0.32}
            stroke={AMBER} strokeWidth="1" strokeOpacity="0.18" strokeDasharray="4 6"
          />
        </g>

        {/* Edge vignettes */}
        <rect x="0" y="0" width={W} height={height} fill={`url(#gc-vL-${uid})`} />
        <rect x="0" y="0" width={W} height={height} fill={`url(#gc-vR-${uid})`} />

        {/* Rail labels — left edge */}
        {showLabels && rails.map((rail) => (
          <text
            key={rail.id}
            x={W * 0.135}
            y={height * rail.y + 3}
            textAnchor="end"
            fill={rail.color}
            fillOpacity="0.45"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '8px', letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            {rail.label}
          </text>
        ))}

        {/* Fix 9 — proof anchor glyph on the control-state rail (the moat).
            Establishes that the brand mark belongs to the rail system, not
            just the marketing surface. Sized 12px, variant-aware. */}
        {showLabels && rails[1] && (
          <foreignObject
            x={W * 0.155}
            y={height * rails[1].y - 6}
            width={12}
            height={12}
            data-testid="continuum-proof-anchor"
          >
            <ApexGlyph size={12} variant={variant === 'lens' ? 'lens' : 'trust'} />
          </foreignObject>
        )}
      </svg>

      <style>{`
        @keyframes gc-node-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.7; }
          50%      { transform: scale(1.4); opacity: 1;   }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="governance-continuum"] * { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
